// src/components/molecules/ChallengeCreator/ChallengeDatesAndPrizingSection.js

import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import CustomCard from "./CustomCard";
import SubmissionFeeSwitch from "./SubmissionFeeSwitch";
import CrowdFundingSwitch from "./CrowdFundingSwitch";

const ChallengeDatesAndPrizingSection = ({
  formData,
  handleChange,
  handleToggleChange,
  handleCrowdFundingToggleChange,
  theme,
  errors,
  disabled, // New prop to disable the form
}) => {
  const [inputValue, setInputValue] = useState(formData.submissionFee || "");

  // Handle input change locally and pass to the parent
  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    handleChange(event); // Pass value to the parent form
  };

  // Handler for applying constraints on blur
  const handleBlur = () => {
    let parsedValue = parseFloat(inputValue);

    // Ensure submissionFee is at least 5 when chargeSubmissionFee is true
    if (
      formData.chargeSubmissionFee &&
      (String(inputValue).trim() === "" ||
        isNaN(parsedValue) ||
        parsedValue < 5)
    ) {
      parsedValue = 5; // Set to 5 if empty or below minimum
    } else if (parsedValue > 1000) {
      parsedValue = 1000; // Cap the maximum at 1000
    }

    setInputValue(parsedValue); // Update local input value with the final value
    handleChange({ target: { name: "submissionFee", value: parsedValue } }); // Pass the final value to parent
  };

  return (
    <CustomCard title="Challenge Prizing & Dates">
      {/* Challenge Prize Details */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            component="label"
            htmlFor="prize"
            sx={{ color: "white", fontFamily: "basic-sans" }}
          >
            Challenge Prize details
          </Typography>
          <input
            type="text"
            id="prize"
            name="prize"
            value={formData.prize}
            onChange={handleChange}
            disabled={disabled} // Disable the field when the prop is true
            maxLength={125}
            placeholder="Be featured in my video!"
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "4px",
              border: errors.prize
                ? `1px solid ${theme.colors.red}`
                : "1px solid #ccc",
              marginTop: "8px",
              fontFamily: "basic-sans",
              backgroundColor: disabled ? "#f0f0f0" : "white", // Change background when disabled
            }}
          />
          {errors.prize && (
            <Typography
              variant="body2"
              sx={{ color: theme.colors.red, mt: 1, fontFamily: "basic-sans" }}
            >
              {errors.prize}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Date Pickers: Start Date, End Date, Fulfillment Date */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            component="label"
            htmlFor="startDate"
            sx={{ color: "white", fontFamily: "basic-sans" }}
          >
            Start Date
          </Typography>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            disabled={disabled} // Disable the field when the prop is true
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "4px",
              border: errors.startDate
                ? `1px solid ${theme.colors.red}`
                : "1px solid #ccc",
              marginTop: "8px",
              fontFamily: "basic-sans",
              backgroundColor: disabled ? "#f0f0f0" : "white", // Change background when disabled
            }}
          />
          {errors.startDate && (
            <Typography
              variant="body2"
              sx={{ color: theme.colors.red, mt: 1, fontFamily: "basic-sans" }}
            >
              {errors.startDate}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography
            variant="body1"
            component="label"
            htmlFor="endDate"
            sx={{ color: "white", fontFamily: "basic-sans" }}
          >
            End Date
          </Typography>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            disabled={disabled} // Disable the field when the prop is true
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "4px",
              border: errors.endDate
                ? `1px solid ${theme.colors.red}`
                : "1px solid #ccc",
              marginTop: "8px",
              fontFamily: "basic-sans",
              backgroundColor: disabled ? "#f0f0f0" : "white", // Change background when disabled
            }}
          />
          {errors.endDate && (
            <Typography
              variant="body2"
              sx={{ color: theme.colors.red, mt: 1, fontFamily: "basic-sans" }}
            >
              {errors.endDate}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography
            variant="body1"
            component="label"
            htmlFor="fulfillmentDate"
            sx={{ color: "white", fontFamily: "basic-sans" }}
          >
            Fulfillment Date
          </Typography>
          <input
            type="date"
            id="fulfillmentDate"
            name="fulfillmentDate"
            value={formData.fulfillmentDate}
            onChange={handleChange}
            disabled={disabled} // Disable the field when the prop is true
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "4px",
              border: errors.fulfillmentDate
                ? `1px solid ${theme.colors.red}`
                : "1px solid #ccc",
              marginTop: "8px",
              fontFamily: "basic-sans",
              backgroundColor: disabled ? "#f0f0f0" : "white", // Change background when disabled
            }}
          />
          {errors.fulfillmentDate && (
            <Typography
              variant="body2"
              sx={{ color: theme.colors.red, mt: 1, fontFamily: "basic-sans" }}
            >
              {errors.fulfillmentDate}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Submission Fee Switch and Additional Text */}
      <Grid container spacing={2} sx={{ mb: 0 }}>
        <Grid item xs={12} md={4}>
          {/* Submission Fee Switch */}
          <SubmissionFeeSwitch
            formData={formData}
            handleToggleChange={handleToggleChange}
            theme={theme}
            disabled={disabled} // Disable the toggle when the prop is true
          />
        </Grid>

        {/* Conditionally render the Submission Fee input field */}
        {formData.chargeSubmissionFee && (
          <Grid item xs={12} md={4}>
            <Typography
              variant="body1"
              component="label"
              htmlFor="submissionFee"
              sx={{
                color: "white",
                fontFamily: "basic-sans",
                textAlign: "left",
                display: "block",
                mb: 1,
              }}
            >
              Submission Fee ($)
            </Typography>
            <input
              type="text"
              id="submissionFee"
              name="submissionFee"
              value={formData.submissionFee || ""}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={disabled} // Disable the field when the prop is true
              placeholder="Set fee in $"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0"
              step="1"
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontFamily: "basic-sans",
                backgroundColor: disabled ? "#f0f0f0" : "white", // Change background when disabled
              }}
            />
            {errors.submissionFee && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.colors.red,
                  mt: 1,
                  fontFamily: "basic-sans",
                }}
              >
                {errors.submissionFee}
              </Typography>
            )}
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          {/* Additional Body Text */}
          <Typography
            variant="body1"
            sx={{
              color: theme.colors.darkGray,
              fontFamily: "basic-sans",
              fontSize: "0.9rem",
              mt: 2,
              textAlign: "left",
              verticalAlign: "bottom",
            }}
          >
            By enabling the submission fee, you can charge participants a fee
            for entering the challenge.
          </Typography>
        </Grid>
      </Grid>

      {/* Crowd Funding Switch and Additional Text */}
      <Grid container spacing={2} sx={{ mb: 0 }}>
        <Grid item xs={12} md={4}>
          {/* Submission Fee Switch */}
          <CrowdFundingSwitch
            formData={formData}
            handleCrowdFundingToggleChange={handleCrowdFundingToggleChange}
            theme={theme}
            disabled={disabled} // Disable the toggle when the prop is true
          />
        </Grid>

        <Grid item xs={12} md={8}>
          {/* Additional Body Text */}
          <Typography
            variant="body1"
            sx={{
              color: theme.colors.darkGray,
              fontFamily: "basic-sans",
              fontSize: "0.9rem",
              mt: 2,
              textAlign: "left",
              verticalAlign: "bottom",
            }}
          >
            By allowing crowd funding, participants can crowd fund their
            submissions to your challenge.
          </Typography>
        </Grid>
      </Grid>
    </CustomCard>
  );
};

export default ChallengeDatesAndPrizingSection;
